import { gqlClientMutations } from '#lib/gqlClientMutations.js';
import { ValidateTurnstileDocument } from '#lib/mutations/__generated__/validateTurnstile.generated.js';
import { isCypress } from '$global/utils/isCypress/isCypress';

/**
 * Reactive Turnstile state + handlers for the `ValidateTurnstile` flow.
 *
 * Drop it into a component and wire the returned handlers/state to a
 * `<Turnstile>` and your submit gate:
 *
 * ```svelte
 * const turnstile = createTurnstile();
 *
 * <Turnstile
 *   publicTurnstileKey={Config.TurnstileKey}
 *   bind:reset={turnstile.resetTurnstile}
 *   onVerify={turnstile.handleTurnstileVerify}
 *   onError={turnstile.handleTurnstileFailure}
 *   onExpired={turnstile.handleTurnstileFailure}
 *   onTimeout={turnstile.handleTurnstileFailure}
 *   hasError={turnstile.turnstileError}
 * />
 *
 * <button disabled={!turnstile.isTurnstileVerified}>Submit</button>
 * ```
 */
export const createTurnstile = () => {
	// Cypress e2e runs bypass the gate — treat as verified from the start.
	let turnstileToken = $state('');
	let isTurnstileVerified = $state(isCypress());
	let turnstileError = $state(false);
	let resetTurnstile = $state<(() => void) | undefined>(undefined);

	const handleTurnstileVerify = async (token: string) => {
		turnstileToken = token;
		turnstileError = false;

		try {
			const { validateTurnstile } = await gqlClientMutations.request(
				ValidateTurnstileDocument,
				{ token }
			);
			isTurnstileVerified = validateTurnstile?.success ?? false;
			turnstileError = !isTurnstileVerified;
		} catch {
			isTurnstileVerified = false;
			turnstileError = true;
		}
	};

	const handleTurnstileFailure = () => {
		turnstileToken = '';
		isTurnstileVerified = false;
		turnstileError = true;
	};

	// Clears the local state and re-renders the widget (tokens are single-use).
	const reset = () => {
		turnstileToken = '';
		turnstileError = false;
		isTurnstileVerified = isCypress();
		resetTurnstile?.();
	};

	return {
		get turnstileToken() {
			return turnstileToken;
		},
		get isTurnstileVerified() {
			return isTurnstileVerified;
		},
		get turnstileError() {
			return turnstileError;
		},
		get resetTurnstile() {
			return resetTurnstile;
		},
		set resetTurnstile(fn: (() => void) | undefined) {
			resetTurnstile = fn;
		},
		handleTurnstileVerify,
		handleTurnstileFailure,
		reset,
	};
};
